import React, { FC, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useStore } from "stores/utils/use-store"
import { Spin, Tabs, Button, Card, Checkbox, Tag, Collapse, Modal, Input } from "antd"
import { observer } from "mobx-react"
import { AdminPasswordModal } from "components"
import { obfuscateAccount } from "domains/accounts/api/accounts.call"
import { AccountTransactionsBuffPlay } from "./account-transactions-BuffPlay"
import { AddBonusBuffPlayComponent } from "../../accounts/components/add-bonus-BuffPlay"
import { defaultFormat } from "utils/date"
import { AccountAnalyticsBuffPlay } from "./account-analytics-BuffPlay"
import { AccountSecurityLogsBuffPlay } from "./account-security-logs-BuffPlay"
import { AccountGamesBuffPlay } from "./account-games-BuffPlay"
import { UpsertPopupBuffPlayComponent } from "domains/accounts/components/upsert-popup-BuffPlay"
import { BuffPlayPopupType } from "../api/popups.call"
import { AccountInstalledPackages } from "./account-installed-packages"
import { notifications } from "notifications"

const PASSWORD_ENV = process.env.REACT_APP_DELETE_FINGERPRINT_PASSWORD || ""
const SESSION_KEY = "isFingerprintDeleteUnlocked"

const { TabPane } = Tabs
const { Panel } = Collapse

export const AccountDetailsBuffPlay: FC = observer<FC>(() => {
  const [visibleAddBonusModal, setAddBonusModalVisibility] = useState<boolean>(false)
  const [upsertPopupModalVisible, setUpsertPopupModalVisible] = useState<boolean>(false)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [passwordVerified, setPasswordVerified] = useState(
    sessionStorage.getItem(SESSION_KEY) === "true",
  )
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState("")
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  const navigate = useNavigate()

  const { id } = useParams<{ id: string }>()

  const { dataStore: { accountDetailsStoreBuffPlay, accountPostbacksStore } } = useStore()
  const [tickingLoginStreak, setTickingLoginStreak] = useState(false)

  useEffect(() => {
    if (!id) {
      return
    }
    accountDetailsStoreBuffPlay.getAccountDetailsById(id).then()
  }, [accountDetailsStoreBuffPlay, id])

  if (!accountDetailsStoreBuffPlay.account) return null
  const { email, createdAt, isBuffPlayOnboarded, isFrozen, usd, utmData }
    = accountDetailsStoreBuffPlay.account

  const onChangeHasFraudActivity = async() => {
    if (id) {
      await accountDetailsStoreBuffPlay.putHasFraudActivityBuffPlay(
        id,
        !accountDetailsStoreBuffPlay.account?.hasFraudActivity,
      )
      await accountDetailsStoreBuffPlay.getAccountDetailsById(id)
    }
  }

  const onChangeIsFaceVerified = async() => {
    if (id) {
      await accountDetailsStoreBuffPlay.putFaceVerificationBuffPlay(
        id,
        !accountDetailsStoreBuffPlay.account?.isFaceVerified,
      )
      await accountDetailsStoreBuffPlay.getAccountDetailsById(id)
    }
  }

  const onChangeIsWhitelisted = async() => {
    if (id) {
      await accountDetailsStoreBuffPlay.putWhitelistedBuffPlay(
        id,
        !accountDetailsStoreBuffPlay.account?.isWhitelisted,
      )
      await accountDetailsStoreBuffPlay.getAccountDetailsById(id)
    }
  }

  const onUpsertPopup = async(el: {text: string, type: BuffPlayPopupType}) => {
    if (id) {
      await accountDetailsStoreBuffPlay.upsertPopup(id, el.type, el.text)
    }
    setUpsertPopupModalVisible(false)
  }

  // Inner "actually delete" path. Skips the password gate so the
  // post-password-verify flow doesn't reopen the password modal a second
  // time due to stale closure of `passwordVerified`.
  const confirmAndDeleteFingerprint = () => {
    if (!id) return
    Modal.confirm({
      title: "Delete fingerprint",
      content: "Are you sure you want to delete all fingerprints matching this account?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async() => {
        try {
          const res = await accountDetailsStoreBuffPlay.deleteFingerprintBuffPlay(id)
          const deletedCount = res?.deletedCount ?? 0
          if (deletedCount === 0) {
            notifications.info("No fingerprints found to delete")
          } else {
            notifications.success(`Deleted ${deletedCount} fingerprint(s)`)
          }
        } catch (err: any) {
          notifications.error(err?.message || "Failed to delete fingerprints")
        }
      },
    })
  }

  const onDeleteFingerprint = () => {
    if (!id) return
    if (!passwordVerified) {
      setPasswordModalOpen(true)

      return
    }
    confirmAndDeleteFingerprint()
  }

  const onTickLoginStreak = () => {
    if (!id) return
    Modal.confirm({
      title: "Tick login streak",
      content: "Tick login streak for this account? This bypasses the once-per-day guard.",
      okText: "Tick",
      cancelText: "Cancel",
      onOk: async() => {
        setTickingLoginStreak(true)
        try {
          await accountPostbacksStore.tickLoginStreak(id)
          notifications.success("Login streak ticked")
        } catch (err: any) {
          notifications.error(err?.message || "Failed to tick login streak")
        } finally {
          setTickingLoginStreak(false)
        }
      },
    })
  }

  const handlePasswordSubmit = (password: string) => {
    const ok = password === String(PASSWORD_ENV)
    if (ok) {
      setPasswordVerified(true)
      sessionStorage.setItem(SESSION_KEY, "true")
      setPasswordModalOpen(false)
      // Jump straight into the actual delete flow — going via
      // onDeleteFingerprint() would re-check the (still-stale)
      // `passwordVerified` state and reopen the password modal.
      confirmAndDeleteFingerprint()
    } else {
      notifications.error("Incorrect password")
    }
  }

  const closeDeleteAccountModal = () => {
    setDeleteAccountModalOpen(false)
    setDeleteConfirmation("")
  }

  const onConfirmDeleteAccount = async() => {
    if (!id) return
    if (deleteConfirmation !== "DELETE") {
      notifications.error("Type DELETE exactly to confirm")

      return
    }
    setIsDeletingAccount(true)
    try {
      await obfuscateAccount(id)
      notifications.success("Account deleted (obfuscated)")
      closeDeleteAccountModal()
      navigate("/buff-play-v2/accounts")
    } catch (err: any) {
      notifications.error(err?.message || "Failed to delete account")
    } finally {
      setIsDeletingAccount(false)
    }
  }

  return (
    <Spin spinning={accountDetailsStoreBuffPlay.isLoading} size="large">
      {!accountDetailsStoreBuffPlay.isLoading ? (
        <>
          <div style={{ marginBottom: 12 }}>
            <Button
              type="primary"
              loading={tickingLoginStreak}
              disabled={tickingLoginStreak}
              onClick={onTickLoginStreak}
            >
              Tick Login Streak
            </Button>
          </div>
          <Tabs style={{ marginBottom: "20px" }}>
            <TabPane tab="General Info" key={-2}>
              <div
                style={{
                  display: "grid",
                  gap: "20px",
                  gridAutoFlow: "column",
                  gridTemplateColumns: "max-content",
                }}
              >
                <Card title="Account Info">
                  <p>ID: {id}</p>
                  <p>Email: {email}</p>
                  <p>
                    <Checkbox
                      style={{ userSelect: "none" }}
                      onChange={onChangeHasFraudActivity}
                      checked={accountDetailsStoreBuffPlay.account?.hasFraudActivity}
                      disabled={accountDetailsStoreBuffPlay.isLoading}
                    >
                      Fraud Activity
                    </Checkbox>
                  </p>
                  <p>
                    <Checkbox
                      style={{ userSelect: "none" }}
                      onChange={onChangeIsFaceVerified}
                      checked={accountDetailsStoreBuffPlay.account?.isFaceVerified}
                      disabled={accountDetailsStoreBuffPlay.isLoading}
                    >
                      Face Verified
                    </Checkbox>
                  </p>
                  <p>
                    <Checkbox
                      style={{ userSelect: "none" }}
                      onChange={onChangeIsWhitelisted}
                      checked={accountDetailsStoreBuffPlay.account?.isWhitelisted}
                      disabled={accountDetailsStoreBuffPlay.isLoading}
                    >
                      Whitelisted
                    </Checkbox>
                  </p>
                  <p>BuffPlayOnboarded: {isBuffPlayOnboarded ? "Yes" : "No"}</p>
                  <p>Frozen: {isFrozen ? "Yes" : "No"}</p>
                  <p>Balance: ${usd}</p>
                  <p>
                    <Tag color="green">Created At: {defaultFormat(createdAt)}</Tag>
                  </p>
                  <p>
                    UTM Data:{" "}
                    <p>
                      <Collapse>
                        <Panel key="1" header="Show content" style={{ width: "350px" }}>
                          <pre>{JSON.stringify(utmData, null, 2)}</pre>
                        </Panel>
                      </Collapse>
                    </p>
                  </p>
                  <Button
                    danger
                    onClick={onDeleteFingerprint}
                  >
                    Delete Fingerprint
                  </Button>
                  <Button
                    danger
                    style={{ marginLeft: 8 }}
                    onClick={() => setDeleteAccountModalOpen(true)}
                  >
                    Delete Account
                  </Button>
                </Card>
                <Card title="Popups">
                  <Button
                    type="primary"
                    onClick={() => setUpsertPopupModalVisible(true)}
                  >
                    Upsert Popup
                  </Button>
                </Card>
              </div>
            </TabPane>
            <TabPane tab="Points Transactions" key="1">
              <AccountTransactionsBuffPlay />
              <Button onClick={() => setAddBonusModalVisibility(true)} style={{ marginTop: 10 }}>
                Create a Transaction
              </Button>
            </TabPane>
            <TabPane tab="Games" key="6">
              <AccountGamesBuffPlay />
            </TabPane>
            <TabPane tab="Analytics" key="4">
              <AccountAnalyticsBuffPlay />
            </TabPane>
            <TabPane tab="Security Logs" key="5">
              <AccountSecurityLogsBuffPlay />
            </TabPane>
            <TabPane tab="Installed Packages" key="7">
              <AccountInstalledPackages />
            </TabPane>
          </Tabs>
        </>
      ) : (
        <div />
      )}
      <AddBonusBuffPlayComponent
        visible={visibleAddBonusModal}
        accountId={id ?? ""}
        onClose={() => setAddBonusModalVisibility(false)}
      />
      <UpsertPopupBuffPlayComponent
        visible={upsertPopupModalVisible}
        onSubmit={onUpsertPopup}
        onClose={() => setUpsertPopupModalVisible(false)}
      />
      <AdminPasswordModal
        open={passwordModalOpen}
        title="Password protection"
        description="Please enter the password to delete fingerprints."
        submitLabel="Submit"
        onSubmit={handlePasswordSubmit}
        onCancel={() => setPasswordModalOpen(false)}
      />
      <Modal
        visible={deleteAccountModalOpen}
        title="Delete Account"
        okText="Delete"
        okType={deleteConfirmation === "DELETE" ? "primary" : "danger"}
        okButtonProps={{ disabled: deleteConfirmation !== "DELETE", loading: isDeletingAccount }}
        cancelText="Cancel"
        onOk={onConfirmDeleteAccount}
        onCancel={closeDeleteAccountModal}
      >
        <p>Account obfuscation: are you sure?</p>
        <p>
          This action obfuscates sensitive information for this account and cannot be undone.
        </p>
        <p>
          Type <span style={{ color: "red" }}>DELETE</span> below and click{" "}
          <span style={{ color: "blue" }}>Delete</span> to confirm.
        </p>
        <Input
          value={deleteConfirmation}
          onChange={e => setDeleteConfirmation(e.target.value)}
          placeholder="DELETE"
        />
      </Modal>
    </Spin>
  )
})
